import "./AbaqueToolsForm.scss";
import Button from "core/buttons/button/Button";
import InfoContext from "pages/InfoContext";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import InputText from "core/inputs/input-text/InputText";
import React, { useContext } from "react";
import PropTypes from "prop-types";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { POPUP_STATUS, TAG_TYPES, VAR_TYPES, LANG } from "constants/constants";
import {
  RULES,
  FORM_INFO,
  ABAQUE_FORM_LABELS,
  JOB_CATEGORY_LABEL,
  JOB_LOCKED_PLACEHOLDER,
} from "constants/forms";
import { Typography } from "core/typography/Typography";
import {
  getTranslatedList,
  formatNames,
  renderInputJobCategoryTag,
} from "components/form/CommonInputs";
import { Controller } from "utils/Controller";
import { getJobsInCategories } from "utils/functions";

const AbaqueToolsForm = ({
  jobList,
  lang,
  branches,
  setStatsHistorySalaryByBranches,
}) => {
  const {
    setCode,
    setStatus,
    getAbaqueFilters,
    control,
    errors,
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    jobCategoryList,
    getCategoriesJobIds,
  } = useContext(InfoContext);

  // The picked categories narrow the job options; none picked leaves them all.
  const selectedCategoryIds = (watch("domain_category_id") || [])
    .map((elm) => elm?.value)
    .filter(Boolean);
  const allowedJobIds = getCategoriesJobIds(selectedCategoryIds);
  const jobOptions = getJobsInCategories(jobList, allowedJobIds);

  // Drop the jobs the categories no longer offer, removing the last category
  // included (redmine 12939). Done here, in the gesture that changes the
  // categories, and NOT in an effect reacting to it: ToolsManager restores the
  // form from the stored filter whenever that filter changes, and React runs a
  // child's effects BEFORE its parent's — so a prune done in an effect was
  // overwritten by the restore that followed, with the stale job list.
  const setJobCategories = (_name, categories) => {
    setValue("domain_category_id", categories);
    const selectedJobs = watch("domain_id") || [];
    if (!selectedJobs.length) return;
    const categoryIds = (categories || [])
      .map((elm) => elm?.value)
      .filter(Boolean);
    const kept = categoryIds.length
      ? getJobsInCategories(selectedJobs, getCategoriesJobIds(categoryIds))
      : [];
    if (kept.length !== selectedJobs.length) setValue("domain_id", kept);
  };

  const onError = () => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };

  const clearAllLabel =
    lang === LANG.EN ? "Unselect all" : "Tout désélectionner";
  const allBranchesOption = {
    label: lang === LANG.EN ? "All branches" : "Toutes les filiales",
    value: "ALL_BRANCHES",
    type: TAG_TYPES.BRANCHES,
    selectAll: true,
  };
  const onSubmit = (data) => {
    setStatsHistorySalaryByBranches([]);
    getAbaqueFilters({
      branch_id: data?.branch_id?.map((e) => e?.value),
      domain_category_id: data?.domain_category_id?.map((e) => e?.value),
      domain_id: data?.domain_id?.map((e) => e?.value),
      experience: Number(data?.experience),
    });
  };

  return (
    <div className="abaque-tools-wrapper">
      <Typography
        className="title-3 violet text-uppercase"
        type="h2"
        label={
          <FormattedMessage
            id="abaque_tools"
            defaultMessage="Outil abaque salaire"
          />
        }
      />
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        style={{ paddingTop: "2rem" }}
      >
        <div className="section-1">
          <div className="cell-padding">
            <Controller
              {...{
                control,
                register,
                name: "experience",
                rules: {
                  required: true,
                },
                render: (props) => (
                  <InputText
                    id="experience"
                    errors={errors["experience"]}
                    label={ABAQUE_FORM_LABELS.experience}
                    type={VAR_TYPES.NUMBER}
                    pattern={RULES.positive_max_99}
                    required
                    {...props}
                  />
                ),
              }}
            />
          </div>
          <div className="cell-padding">
            <Controller
              {...{
                control,
                register,
                name: "branch_id",
                rules: {
                  required: true,
                },
                render: () => (
                  <InputSearchTag
                    errors={errors["branch_id"]}
                    label={ABAQUE_FORM_LABELS.branch_id}
                    limitChoice={99}
                    onDelete={(val) => {
                      const updatedBranches = watch("branch_id").filter(
                        (e) => e?.value !== val
                      );
                      setValue("branch_id", updatedBranches);
                    }}
                    options={[allBranchesOption, ...formatNames(branches)]}
                    popup={FORM_INFO.branch.popup}
                    selectedOptions={watch("branch_id")}
                    setValue={(val) => {
                      setValue("branch_id", val);
                      clearErrors("branch_id");
                    }}
                    type={TAG_TYPES.BRANCHES}
                    id="branch-filter"
                    clearAllLabel={clearAllLabel}
                    required
                  />
                ),
              }}
            />
          </div>
          <div className="cell-padding">
            {renderInputJobCategoryTag({
              control,
              register,
              filter: "domain_category_id",
              jobCategories: jobCategoryList,
              label: JOB_CATEGORY_LABEL,
              limitChoice: 99,
              selectedOptions: watch("domain_category_id"),
              setValue: setJobCategories,
              lang,
              clearAllLabel,
              // Carries the requirement of the search now that the job cannot
              // (redmine 12939).
              required: true,
              errors,
              clearErrors,
            })}
          </div>
          <div className="cell-padding">
            <Controller
              {...{
                control,
                register,
                name: "domain_id",
                // Not required any more: the search criterion moved onto the
                // category, which is entered first and gates this field
                // (redmine 12939). Requiring a field the user cannot reach
                // would deadlock the form.
                rules: {
                  required: false,
                },
                render: () => (
                  <InputSearchTag
                    errors={errors["domain_id"]}
                    label={ABAQUE_FORM_LABELS.domain_id}
                    limitChoice={99}
                    onDelete={() => setValue("domain_id", [])}
                    // No "all jobs" pseudo-value any more: it only ever showed
                    // while nothing narrowed the list, which is now exactly
                    // when the field is locked (redmine 12939). Searching the
                    // whole of a category is done by picking it and leaving
                    // this empty.
                    options={getTranslatedList(jobOptions, lang)}
                    popup={FORM_INFO.dav_function.popup}
                    // The category filter comes first: it is what narrows this
                    // list (redmine 12939).
                    disabled={selectedCategoryIds.length === 0}
                    placeholder={
                      selectedCategoryIds.length === 0
                        ? JOB_LOCKED_PLACEHOLDER
                        : undefined
                    }
                    selectedOptions={watch("domain_id")}
                    setValue={(val) => {
                      setValue("domain_id", val);
                      clearErrors("domain_id");
                    }}
                    type={TAG_TYPES.FUNCTIONS}
                    id="job-filter"
                    clearAllLabel={clearAllLabel}
                    required={false}
                  />
                ),
              }}
            />
          </div>
        </div>
        <div className="div-buttons">
          <Button
            color="btn-medium gray-700"
            type={"submit"}
            id="button-search"
          >
            <FormattedMessage id="search-button" defaultMessage="Rechercher" />
          </Button>
        </div>
      </form>
    </div>
  );
};

AbaqueToolsForm.propTypes = {
  jobList: PropTypes.array,
  lang: PropTypes.string,
  branches: PropTypes.array,
  setStatsHistorySalaryByBranches: PropTypes.func,
};

export default AbaqueToolsForm;
